import { useMemo } from 'react';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { ModalSwipeLayout } from '@/kit/Modal/layouts/swipe';
import { ModalCenterLayout } from '@/kit/Modal/layouts/center';
import { useModal } from '@/providers/modal';
import { MODAL_IDS } from '@/kit/Modal/keys';
import { useWindowSize } from '@/hooks/useWindowSize';
import { Button, CountrySelect } from '@scramble/ui';
import IconGlobe from '@scramble/design-system/icons/globe-06.svg';
import { useGetCountries } from '@/query/queries/common/useGetCountries';
import { VeriffButton } from '@/modals/CountryVerificationModal/veriff';
import { useVeriffStatus } from '@/hooks/shared/useVeriffStatus';
import { useGetMe } from '@/hooks/useGetMe';
import s from './index.module.scss';

const id = MODAL_IDS.country_verification;

export const CountryVerificationModal = props => {
  const { modalIds } = useModal();
  return modalIds[id] && <Modal {...props} />;
};

export const Modal = ({ onSuccess }) => {
  const { t } = useTranslation();
  const { isMobile } = useWindowSize();
  const { data: countries } = useGetCountries();
  const { closeModal } = useModal();
  const { user } = useGetMe();
  const { ready, isVeriffApplyRequired } = useVeriffStatus();

  const allowVeriff = ready && isVeriffApplyRequired && user;

  const grouped = useMemo(() => {
    if (!countries) return null;

    return countries.reduce(
      (acc, country) => {
        if (country.is_restricted) {
          acc.restricted.push(country);
          acc.restrictedMap.push(country.code);
        } else {
          acc.allowed.push(country);
        }

        return acc;
      },
      { restricted: [], allowed: [], restrictedMap: [] },
    );
  }, [countries]);

  const { values, errors, setFieldValue, handleSubmit } = useFormik({
    initialValues: {
      citizenship: null,
      residence: null,
    },
    validate: vals => {
      const errs = {};
      if (
        vals.citizenship &&
        grouped?.restrictedMap.includes(vals.citizenship.code)
      ) {
        errs.citizenship = t(
          'modal:country_verification_modal.restriction_note',
        );
      }
      return errs;
    },
    validateOnChange: true,
    validateOnBlur: false,
    onSubmit: () => {},
  });

  const isRestrictedCitizenship = grouped?.restrictedMap.includes(
    values.citizenship?.code,
  );

  const canContinue =
    values.citizenship && (!isRestrictedCitizenship || values.residence);

  const content = (
    <div className={s.modal}>
      <div className={s.content}>
        <div className={s.content_inner}>
          <div className={s.header}>
            <div className={s.icon}>
              <img src={IconGlobe} alt="icon" width={20} height={20} />
            </div>
            <h1 className={s.title}>
              {t('modal:country_verification_modal.title')}
            </h1>
            <p className={s.desc}>{t('modal:country_verification_modal.desc')}</p>
          </div>

          <form className={s.form} onSubmit={handleSubmit}>
            <CountrySelect
              label={t('modal:country_verification_modal.citizenship_label')}
              placeholder={t(
                'modal:country_verification_modal.select_placeholder',
              )}
              isRequired
              selected={values.citizenship}
              onChange={country => setFieldValue('citizenship', country)}
              error={errors?.citizenship}
              options={countries}
              menuPlacement="top"
            />
            {values.citizenship && isRestrictedCitizenship && (
              <CountrySelect
                label={t('modal:country_verification_modal.residence_label')}
                placeholder={t(
                  'modal:country_verification_modal.select_placeholder',
                )}
                isRequired
                selected={values.residence}
                onChange={country => setFieldValue('residence', country)}
                error={errors?.residence}
                options={grouped?.allowed}
                menuPlacement="top"
              />
            )}
          </form>
        </div>
      </div>
      <div className={s.footer}>
        {canContinue ? (
          allowVeriff && (
            <VeriffButton
              user={user}
              onSuccess={onSuccess}
              onClose={() => closeModal(id)}
            />
          )
        ) : (
          <Button
            theme="neutral"
            appearance="filled"
            size="lg"
            label={t('modal:country_verification_modal.continue')}
            disabled={!canContinue}
            onClick={() => closeModal(id)}
          />
        )}
      </div>
    </div>
  );

  if (!user) return null;

  return isMobile ? (
    <ModalSwipeLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalSwipeLayout>
  ) : (
    <ModalCenterLayout id={id} onClose={() => closeModal(id)} showClose>
      {content}
    </ModalCenterLayout>
  );
};
